import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Career.css";
// bring in loan-apply-form styles so popup matches home design exactly
import "../common/loan-apply-form/loan-apply-form.css";

const Career = () => {
  const navigate = useNavigate();

  // Departments filter
  const [selectedDepartment, setSelectedDepartment] = useState("all");

  // modal state for popup version of form
  const [isCareerModalOpen, setIsCareerModalOpen] = useState(false);
  const [careerStep, setCareerStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    panCard: null,
    aadhaarFront: null,
    aadhaarBack: null,
    profilePhoto: null,
    position: "",
    experience: "",
    currentCompany: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    coverLetter: "",
    linkedin: "",
    portfolio: "",
    terms: false,
  });

  const allowedDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "protonmail.com",
    "mail.com",
    "yandex.com",
    "rediffmail.com",
  ];

  // Validation errors state
  const [errors, setErrors] = useState({});
  const [showDomainSuggestions, setShowDomainSuggestions] = useState(false);
  const [localPart, setLocalPart] = useState("");
  const [filteredDomains, setFilteredDomains] = useState(allowedDomains);

  // File states
  const [resumeFile, setResumeFile] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [panCardFile, setPanCardFile] = useState(null);
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [offerLetterFileName, setOfferLetterFileName] = useState("");
  const [panCardFileName, setPanCardFileName] = useState("");
  const [aadhaarFrontFileName, setAadhaarFrontFileName] = useState("");
  const [aadhaarBackFileName, setAadhaarBackFileName] = useState("");

  // Additional dynamic sections
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [salarySlips, setSalarySlips] = useState([
    { id: Date.now(), month: "", file: null, fileName: "" },
  ]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  // Job data
  const departments = [
    { id: "all", name: "All Departments" },
    { id: "sales", name: "Sales & Business" },
    { id: "operations", name: "Operations" },
    { id: "finance", name: "Finance" },
    { id: "support", name: "Customer Support" },
  ];

  const jobOpenings = [
    {
      id: 1,
      title: "Loan Sales Executive",
      department: "sales",
      tag: "🔥 Hot Opening",
      location: "Mumbai, Delhi, Bangalore",
      experience: "1-3 years",
      salary: "₹3L - ₹6L",
      description:
        "Generate leads, meet potential clients, and convert them into loan customers. Achieve monthly sales targets.",
    },
    {
      id: 2,
      title: "Relationship Manager",
      department: "sales",
      tag: "💼 Managerial",
      location: "All Major Cities",
      experience: "3-5 years",
      salary: "₹6L - ₹12L",
      description:
        "Manage banking relationships, grow portfolio, and ensure customer satisfaction for high-value clients.",
    },
    {
      id: 3,
      title: "Business Development Manager",
      department: "sales",
      tag: "🚀 Leadership",
      location: "PAN India",
      experience: "5-8 years",
      salary: "₹10L - ₹20L",
      description:
        "Develop business strategies, manage team of executives, and drive overall business growth.",
    },
    {
      id: 4,
      title: "Operations Executive",
      department: "operations",
      tag: "📋 Operations",
      location: "Mumbai HQ",
      experience: "1-3 years",
      salary: "₹2.5L - ₹4.5L",
      description:
        "Handle loan processing, documentation, and ensure smooth operations of all loan applications.",
    },
    {
      id: 5,
      title: "Credit Analyst",
      department: "finance",
      tag: "💰 Finance",
      location: "Mumbai HQ",
      experience: "2-4 years",
      salary: "₹4L - ₹8L",
      description:
        "Analyze credit applications, assess risk, and make recommendations for loan approvals.",
    },
    {
      id: 6,
      title: "Customer Support Executive",
      department: "support",
      tag: "👥 Support",
      location: "Work From Home",
      experience: "0-2 years",
      salary: "₹2L - ₹3.5L",
      description:
        "Handle customer queries, provide support for loan applications, and ensure customer satisfaction.",
    },
  ];

  const benefits = [
    {
      icon: "fas fa-trophy",
      title: "Competitive Salary",
      description:
        "Industry-best compensation with performance bonuses, incentives, and annual appraisals",
    },
    {
      icon: "fas fa-chart-line",
      title: "Rapid Growth",
      description:
        "Clear career progression with promotions every 12-18 months based on performance",
    },
    {
      icon: "fas fa-graduation-cap",
      title: "Training & Development",
      description:
        "Comprehensive training programs, workshops, and certifications fully sponsored by company",
    },
    {
      icon: "fas fa-heartbeat",
      title: "Health & Wellness",
      description:
        "Health insurance for you and your family, wellness programs, and regular health checkups",
    },
    {
      icon: "fas fa-plane",
      title: "Work-Life Balance",
      description:
        "Flexible working hours, work from home options, and generous paid time off policy",
    },
    {
      icon: "fas fa-award",
      title: "Recognition & Rewards",
      description:
        "Monthly awards, performance bonuses, employee of the year awards with international trips",
    },
  ];

  const filteredJobs =
    selectedDepartment === "all"
      ? jobOpenings
      : jobOpenings.filter((job) => job.department === selectedDepartment);

  // Validation function (can be scoped by step)
  const validateForm = (step = careerStep) => {
    const newErrors = {};

    // step1: personal info only
    if (step === 1) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = "Full name is required";
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email = "Enter a valid email address";
      } else {
        const domain = formData.email.split("@")[1];
        if (!allowedDomains.includes(domain)) {
          newErrors.email =
            "Please use an email from a supported provider (e.g., gmail.com, yahoo.com, outlook.com)";
        }
      }

      const mobileRegex = /^[0-9]{10}$/;
      if (!formData.mobile.trim()) {
        newErrors.mobile = "Mobile number is required";
      } else if (!mobileRegex.test(formData.mobile)) {
        newErrors.mobile = "Enter a valid 10-digit mobile number";
      }

      if (!formData.position) {
        newErrors.position = "Please select a position";
      }

      if (!formData.experience) {
        newErrors.experience = "Please enter total experience";
      }

      if (!formData.currentCTC) {
        newErrors.currentCTC = "Please enter current CTC";
      }

      if (!formData.expectedCTC) {
        newErrors.expectedCTC = "Please enter expected CTC";
      }

      if (!formData.noticePeriod) {
        newErrors.noticePeriod = "Please select notice period duration";
      }
    }

    // step2: document uploads and terms
    if (step === 2) {
      if (!resumeFile) {
        newErrors.resume = "Resume is required";
      }

      // Profile photo validation - REQUIRED
      if (!profilePhotoFile) {
        newErrors.profilePhoto = "Profile photo is required";
      } else {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(profilePhotoFile.type)) {
          newErrors.profilePhoto = "Please upload only JPG or PNG images";
        } else if (profilePhotoFile.size > 2 * 1024 * 1024) {
          newErrors.profilePhoto = "Profile photo must be less than 2MB";
        }
      }

      // PAN card validation
      if (panCardFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(panCardFile.type)) {
          newErrors.panCard = "Please upload only JPG or PNG images";
        } else if (panCardFile.size > 2 * 1024 * 1024) {
          newErrors.panCard = "PAN card image must be less than 2MB";
        }
      }

      // Aadhaar front validation
      if (aadhaarFrontFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(aadhaarFrontFile.type)) {
          newErrors.aadhaarFront = "Please upload only JPG or PNG images";
        } else if (aadhaarFrontFile.size > 2 * 1024 * 1024) {
          newErrors.aadhaarFront = "Aadhaar front image must be less than 2MB";
        }
      }

      // Aadhaar back validation
      if (aadhaarBackFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(aadhaarBackFile.type)) {
          newErrors.aadhaarBack = "Please upload only JPG or PNG images";
        } else if (aadhaarBackFile.size > 2 * 1024 * 1024) {
          newErrors.aadhaarBack = "Aadhaar back image must be less than 2MB";
        }
      }

      // Offer letter validation
      if (offerLetterFile) {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        if (!allowedTypes.includes(offerLetterFile.type)) {
          newErrors.offerLetter = "Please upload only PDF, DOC or DOCX files";
        } else if (offerLetterFile.size > 5 * 1024 * 1024) {
          newErrors.offerLetter = "Offer letter must be less than 5MB";
        }
      }

      if (!formData.terms) {
        newErrors.terms = "You must agree to the terms";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return errors[fieldName] ? "error" : "";
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // For mobile field, only allow numbers
    let finalValue = value;
    if (name === "mobile") {
      finalValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : finalValue,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle domain suggestion click
  const handleDomainSelect = (domain) => {
    setFormData((prev) => ({ ...prev, email: localPart + "@" + domain }));
    setShowDomainSuggestions(false);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, email: value }));

    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
  };

  // Currency formatting
  const formatCurrency = (value) => {
    const numericValue = value.replace(/[^\d]/g, "");
    if (!numericValue) return "";
    return parseInt(numericValue).toLocaleString("en-IN");
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = formatCurrency(value);
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  // File handlers (similar to original but with validation)
  const handleFileChange = (
    e,
    setter,
    setFileName,
    allowedTypes,
    maxSize,
    fieldName = "",
  ) => {
    const file = e.target.files[0];
    if (!file) {
      setter(null);
      setFileName("");
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage(
        `Please upload only ${allowedTypes.map((t) => t.split("/")[1]).join(", ")} files`,
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > maxSize) {
      setErrorModalMessage(
        `File size must be less than ${maxSize / (1024 * 1024)}MB`,
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setter(file);
    setFileName(
      `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
    );

    // Clear resume error if it was there
    if (fieldName === "resume") {
      setErrors((prev) => ({ ...prev, resume: "" }));
    }
  };

  // Resume specific handler
  const handleResumeChange = (e) => {
    handleFileChange(
      e,
      setResumeFile,
      setFileName,
      [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      5 * 1024 * 1024,
      "resume",
    );
  };

  // PAN card handler
  const handlePanCardChange = (e) => {
    handleFileChange(
      e,
      setPanCardFile,
      setPanCardFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    // Update formData with file name (optional)
    if (e.target.files[0]) {
      setFormData((prev) => ({ ...prev, panCard: e.target.files[0].name }));
    }
  };

  // Aadhaar front handler
  const handleAadhaarFrontChange = (e) => {
    handleFileChange(
      e,
      setAadhaarFrontFile,
      setAadhaarFrontFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    if (e.target.files[0]) {
      setFormData((prev) => ({
        ...prev,
        aadhaarFront: e.target.files[0].name,
      }));
    }
  };

  // Aadhaar back handler
  const handleAadhaarBackChange = (e) => {
    handleFileChange(
      e,
      setAadhaarBackFile,
      setAadhaarBackFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    if (e.target.files[0]) {
      setFormData((prev) => ({ ...prev, aadhaarBack: e.target.files[0].name }));
    }
  };

  // Offer letter handler
  const handleOfferLetterChange = (e) => {
    handleFileChange(
      e,
      setOfferLetterFile,
      setOfferLetterFileName,
      [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      5 * 1024 * 1024,
    );
  };

  // Profile photo upload via file input (simplified, removed camera)
  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage("Please upload only JPG or PNG images");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrorModalMessage("Profile photo must be less than 2MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setProfilePhotoFile(file);
    setFormData((prev) => ({ ...prev, profilePhoto: file.name }));
    setProfilePhotoPreview(URL.createObjectURL(file));

    // Clear error if it was there
    if (errors.profilePhoto) {
      setErrors((prev) => ({ ...prev, profilePhoto: "" }));
    }
  };

  // Additional Documents
  const addAdditionalDocument = () => {
    setAdditionalDocs([
      ...additionalDocs,
      { id: Date.now(), type: "", file: null, fileName: "" },
    ]);
  };

  const removeAdditionalDocument = (id) => {
    setAdditionalDocs(additionalDocs.filter((doc) => doc.id !== id));
  };

  const handleAdditionalDocTypeChange = (id, value) => {
    setAdditionalDocs(
      additionalDocs.map((doc) =>
        doc.id === id ? { ...doc, type: value } : doc,
      ),
    );
  };

  const handleAdditionalDocFileChange = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage(
        "Please upload only PDF, DOC, DOCX, JPG or PNG files",
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorModalMessage("File size must be less than 5MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setAdditionalDocs(
      additionalDocs.map((doc) =>
        doc.id === id
          ? {
              ...doc,
              file,
              fileName: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
            }
          : doc,
      ),
    );
  };

  // Salary Slips
  const addSalarySlip = () => {
    if (salarySlips.length >= 6) return;
    setSalarySlips([
      ...salarySlips,
      { id: Date.now(), month: "", file: null, fileName: "" },
    ]);
  };

  const removeSalarySlip = (id) => {
    if (salarySlips.length <= 1) return;
    setSalarySlips(salarySlips.filter((slip) => slip.id !== id));
  };

  const handleSalarySlipMonthChange = (id, value) => {
    setSalarySlips(
      salarySlips.map((slip) =>
        slip.id === id ? { ...slip, month: value } : slip,
      ),
    );
  };

  const handleSalarySlipFileChange = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage("Please upload only PDF, JPG or PNG files");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorModalMessage("File size must be less than 5MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setSalarySlips(
      salarySlips.map((slip) =>
        slip.id === id
          ? {
              ...slip,
              file,
              fileName: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
            }
          : slip,
      ),
    );
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      mobile: "",
      panCard: null,
      aadhaarFront: null,
      aadhaarBack: null,
      profilePhoto: null,
      position: "",
      experience: "",
      currentCompany: "",
      currentCTC: "",
      expectedCTC: "",
      noticePeriod: "",
      coverLetter: "",
      linkedin: "",
      portfolio: "",
      terms: false,
    });
    setResumeFile(null);
    setProfilePhotoFile(null);
    setOfferLetterFile(null);
    setPanCardFile(null);
    setAadhaarFrontFile(null);
    setAadhaarBackFile(null);
    setProfilePhotoPreview("");
    setFileName("");
    setOfferLetterFileName("");
    setPanCardFileName("");
    setAadhaarFrontFileName("");
    setAadhaarBackFileName("");
    setAdditionalDocs([]);
    setSalarySlips([{ id: Date.now(), month: "", file: null, fileName: "" }]);
    setErrors({});
  };

  // open / close modal helpers
  const openCareerModal = (jobTitle) => {
    setFormData((prev) => ({ ...prev, position: jobTitle }));
    setCareerStep(1);
    setIsCareerModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeCareerModal = () => {
    setIsCareerModalOpen(false);
    setCareerStep(1);
    document.body.style.overflow = "auto";
    resetForm();
  };

  // Handle apply button click
  const handleApplyClick = (jobTitle) => {
    openCareerModal(jobTitle);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // when using modal, we only submit when on final step
    if (careerStep !== 2) {
      if (validateForm()) {
        setCareerStep(2);
      }
      return;
    }

    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const element = document.getElementById(firstError);
      if (element)
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);

    const submitData = new FormData();
    submitData.append("name", formData.fullName);
    submitData.append("email", formData.email);
    submitData.append("mobile_number", formData.mobile);
    submitData.append("position_applying_for", formData.position);
    if (formData.experience)
      submitData.append("total_experience_years", formData.experience);
    if (formData.currentCompany)
      submitData.append("current_company", formData.currentCompany);
    if (formData.currentCTC)
      submitData.append(
        "current_ctc",
        formData.currentCTC.replace(/[^\d]/g, "") || "0",
      );
    if (formData.expectedCTC)
      submitData.append(
        "expected_ctc",
        formData.expectedCTC.replace(/[^\d]/g, "") || "0",
      );
    if (formData.noticePeriod)
      submitData.append("notice_period", formData.noticePeriod);
    if (formData.coverLetter)
      submitData.append("cover_letter", formData.coverLetter);
    if (formData.linkedin)
      submitData.append("linkedin_profile", formData.linkedin);
    if (formData.portfolio)
      submitData.append("portfolio_website", formData.portfolio);
    submitData.append("agreed", formData.terms ? "1" : "0");

    if (resumeFile) submitData.append("resume", resumeFile);
    if (profilePhotoFile) submitData.append("selfie", profilePhotoFile);
    if (offerLetterFile)
      submitData.append("other_documents[]", offerLetterFile);
    if (panCardFile) submitData.append("pan_card", panCardFile);
    if (aadhaarFrontFile)
      submitData.append("aadhar_card_front", aadhaarFrontFile);
    if (aadhaarBackFile) submitData.append("aadhar_card_back", aadhaarBackFile);

    salarySlips.forEach((slip, index) => {
      if (slip.file) submitData.append(`salary_slips[${index}]`, slip.file);
    });

    additionalDocs.forEach((doc, index) => {
      if (doc.file) {
        submitData.append(`other_documents[${index}][file]`, doc.file);
        submitData.append(
          `other_documents[${index}][document_type]`,
          doc.type || "Other",
        );
        submitData.append(
          `other_documents[${index}][document_name]`,
          doc.file.name,
        );
      }
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/job-seeker/register`,
        {
          method: "POST",
          body: submitData,
        },
      );

      const result = await response.json();

      if (response.ok) {
        // close popup first
        closeCareerModal();
        setShowSuccessModal(true);
        resetForm();
      } else {
        let errorMessage = "Error submitting application. Please try again.";
        if (result.errors) {
          const firstErrorKey = Object.keys(result.errors)[0];
          const firstError = result.errors[firstErrorKey];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (result.message) {
          errorMessage = result.message;
        }
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      }
    } catch (error) {
      setErrorModalMessage(
        "Network error. Please check your connection and try again.",
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Animation effect (same as original)
  useEffect(() => {
    const animateOnScroll = () => {
      const elements = document.querySelectorAll(
        ".career-benefit-card, .career-job-card, .career-info-card",
      );
      elements.forEach((element) => {
        const elementPosition = element.getBoundingClientRect().top;
        const screenPosition = window.innerHeight / 1.2;
        if (elementPosition < screenPosition) {
          element.style.opacity = "1";
          element.style.transform = "translateY(0)";
        }
      });
    };

    const elements = document.querySelectorAll(
      ".career-benefit-card, .career-job-card, .career-info-card",
    );
    elements.forEach((element) => {
      element.style.opacity = "0";
      element.style.transform = "translateY(30px)";
      element.style.transition = "opacity 0.6s ease, transform 0.6s ease";
    });

    window.addEventListener("load", animateOnScroll);
    window.addEventListener("scroll", animateOnScroll);
    setTimeout(animateOnScroll, 100);

    return () => {
      window.removeEventListener("load", animateOnScroll);
      window.removeEventListener("scroll", animateOnScroll);
    };
  }, []);

  // navigation helpers for steps
  const goToNextStep = () => {
    if (validateForm()) {
      setCareerStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPrevStep = () => {
    setCareerStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  return (
    <>
      {/* Career apply modal (reuse dsa styles for identical look) */}
      {isCareerModalOpen && (
        <div className="dsa-modal-overlay">
          <div
            className="dsa-modal ln-app-card"
            style={{
              width: "100%",
              maxWidth: "700px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {/* header and stepper from loan form design */}
            <div className="ln-app-header">
              <h2>Apply for Job</h2>
              <p>{careerStep === 1 ? "Personal Info" : "Upload Documents"}</p>
            </div>
            <div className="ln-app-stepper">
              {[0, 1].map((s) => (
                <div
                  key={s}
                  className={`ln-step-item ${careerStep - 1 >= s ? "active" : ""}`}
                >
                  <div className="ln-step-circle">{s + 1}</div>
                </div>
              ))}
              <div
                className="ln-step-line"
                style={{ width: `${((careerStep - 1) / (2 - 1)) * 100}%` }}
              ></div>
            </div>
            <button className="close-modal-btn" onClick={closeCareerModal}>
              &times;
            </button>
            <div className="ln-app-form">
              <form
                id="careerForm"
                className="ln-app-form"
                onSubmit={handleSubmit}
                noValidate
              >
                {/* step 1 personal info */}
                {careerStep === 1 && (
                  <div className="ln-app-step-content animation-fade">
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Full Name(As Par Pan Card){" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`ln-app-input ${getErrorClass("fullName")}`}
                        placeholder="Rajesh Sharma"
                        style={
                          errors.fullName
                            ? { borderColor: "#ef4444", borderWidth: "2px" }
                            : {}
                        }
                      />
                      {errors.fullName && (
                        <span className="ln-app-error">{errors.fullName}</span>
                      )}
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Email Address{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleEmailChange}
                          className={`ln-app-input ${getErrorClass("email")}`}
                          placeholder="example@mail.com"
                          autoComplete="off"
                          style={
                            errors.email
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.email && (
                          <span className="ln-app-error">{errors.email}</span>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Mobile Number{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          className={`ln-app-input ${getErrorClass("mobile")}`}
                          placeholder="9737304440"
                          maxLength="10"
                          pattern="[0-9]*"
                          inputMode="numeric"
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          style={
                            errors.mobile
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.mobile && (
                          <span className="ln-app-error">{errors.mobile}</span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Position Applying For
                        </label>
                        <input
                          type="text"
                          id="position"
                          name="position"
                          value={formData.position}
                          readOnly
                          className="ln-app-input"
                        />
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">Total Experience</label>
                        <select
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                          className={`ln-app-select ${errors.experience ? "error" : ""}`}
                        >
                          <option value="">Select Experience</option>
                          <option value="Fresher">Fresher (0-1 years)</option>
                          <option value="1-3">1-3 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="5-8">5-8 years</option>
                          <option value="8+">8+ years</option>
                        </select>
                        {errors.experience && (
                          <span className="ln-app-error">
                            {errors.experience}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">Current Company</label>
                        <input
                          type="text"
                          id="currentCompany"
                          name="currentCompany"
                          value={formData.currentCompany}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="Current company name"
                        />
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">Current CTC (₹)</label>
                        <input
                          type="text"
                          id="currentCTC"
                          name="currentCTC"
                          value={formData.currentCTC}
                          onChange={handleCurrencyChange}
                          className={`ln-app-input ${getErrorClass("currentCTC")}`}
                          placeholder="e.g., 5,00,000"
                          style={
                            errors.currentCTC
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.currentCTC && (
                          <span className="ln-app-error">
                            {errors.currentCTC}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">Expected CTC (₹)</label>
                        <input
                          type="text"
                          id="expectedCTC"
                          name="expectedCTC"
                          value={formData.expectedCTC}
                          onChange={handleCurrencyChange}
                          className={`ln-app-input ${getErrorClass("expectedCTC")}`}
                          placeholder="e.g., 7,00,000"
                          style={
                            errors.expectedCTC
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.expectedCTC && (
                          <span className="ln-app-error">
                            {errors.expectedCTC}
                          </span>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">Notice Period</label>
                        <select
                          id="noticePeriod"
                          name="noticePeriod"
                          value={formData.noticePeriod}
                          onChange={handleInputChange}
                          className={`ln-app-select ${getErrorClass("noticePeriod")}`}
                          style={
                            errors.noticePeriod
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        >
                          <option value="">Select Notice Period</option>
                          <option value="Immediate">Immediate Joining</option>
                          <option value="15">15 days</option>
                          <option value="30">30 days</option>
                          <option value="45">45 days</option>
                          <option value="60">60 days</option>
                          <option value="90">90 days</option>
                        </select>
                        {errors.noticePeriod && (
                          <span className="ln-app-error">
                            {errors.noticePeriod}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* step 2 documents */}
                {careerStep === 2 && (
                  <div className="ln-app-step-content animation-fade">
                    {/* Resume Upload */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Upload Resume{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <div
                        className="ln-app-file-upload"
                        style={{
                          border: errors.resume
                            ? "2px dashed #ef4444"
                            : "2px dashed #cbd5e1",
                          padding: "20px",
                          borderRadius: "10px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="file"
                          id="resume"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeChange}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor="resume"
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          <i
                            className="fas fa-cloud-upload-alt"
                            style={{
                              fontSize: "24px",
                              color: errors.resume ? "#ef4444" : "#4F46E5",
                              marginBottom: "10px",
                            }}
                          ></i>
                          <p
                            style={{
                              margin: "10px 0",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            Click to upload your resume
                          </p>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            PDF, DOC or DOCX (Max 5MB)
                          </p>
                        </label>
                      </div>
                      {fileName && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#10b981",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {fileName}
                        </p>
                      )}
                      {errors.resume && (
                        <span className="ln-app-error">{errors.resume}</span>
                      )}
                    </div>

                    {/* PAN Card Upload */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">PAN Card</label>
                      <div
                        className="ln-app-file-upload"
                        style={{
                          border: "2px dashed #cbd5e1",
                          padding: "20px",
                          borderRadius: "10px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="file"
                          id="panCardModal"
                          accept="image/*"
                          onChange={handlePanCardChange}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor="panCardModal"
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          <i
                            className="fas fa-id-card"
                            style={{
                              fontSize: "24px",
                              color: "#4F46E5",
                              marginBottom: "10px",
                            }}
                          ></i>
                          <p
                            style={{
                              margin: "10px 0",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            Upload PAN Card
                          </p>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            JPG, PNG (Max 2MB)
                          </p>
                        </label>
                      </div>
                      {panCardFileName && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#10b981",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {panCardFileName}
                        </p>
                      )}
                    </div>

                    {/* Aadhaar Card Front & Back */}
                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Aadhaar Card Front
                        </label>
                        <div
                          className="ln-app-file-upload"
                          style={{
                            border: "2px dashed #cbd5e1",
                            padding: "15px",
                            borderRadius: "10px",
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            id="aadhaarFrontModal"
                            accept="image/*"
                            onChange={handleAadhaarFrontChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="aadhaarFrontModal"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <i
                              className="fas fa-address-card"
                              style={{
                                fontSize: "20px",
                                color: "#4F46E5",
                                marginBottom: "8px",
                              }}
                            ></i>
                            <p
                              style={{
                                margin: "8px 0",
                                color: "#475569",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              Upload Front
                            </p>
                            <p
                              style={{
                                margin: "5px 0",
                                color: "#94a3b8",
                                fontSize: "11px",
                              }}
                            >
                              Max 2MB
                            </p>
                          </label>
                        </div>
                        {aadhaarFrontFileName && (
                          <p
                            style={{
                              marginTop: "8px",
                              color: "#10b981",
                              fontSize: "12px",
                            }}
                          >
                            ✓ {aadhaarFrontFileName}
                          </p>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Aadhaar Card Back
                        </label>
                        <div
                          className="ln-app-file-upload"
                          style={{
                            border: "2px dashed #cbd5e1",
                            padding: "15px",
                            borderRadius: "10px",
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            id="aadhaarBackModal"
                            accept="image/*"
                            onChange={handleAadhaarBackChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="aadhaarBackModal"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <i
                              className="fas fa-address-card"
                              style={{
                                fontSize: "20px",
                                color: "#4F46E5",
                                marginBottom: "8px",
                              }}
                            ></i>
                            <p
                              style={{
                                margin: "8px 0",
                                color: "#475569",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              Upload Back
                            </p>
                            <p
                              style={{
                                margin: "5px 0",
                                color: "#94a3b8",
                                fontSize: "11px",
                              }}
                            >
                              Max 2MB
                            </p>
                          </label>
                        </div>
                        {aadhaarBackFileName && (
                          <p
                            style={{
                              marginTop: "8px",
                              color: "#10b981",
                              fontSize: "12px",
                            }}
                          >
                            ✓ {aadhaarBackFileName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Profile Photo */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Profile Photo{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: errors.profilePhoto
                              ? "2px dashed #ef4444"
                              : "2px dashed #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8fafc",
                          }}
                        >
                          {profilePhotoPreview ? (
                            <img
                              src={profilePhotoPreview}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-user"
                              style={{
                                fontSize: "40px",
                                color: errors.profilePhoto
                                  ? "#ef4444"
                                  : "#94a3b8",
                              }}
                            ></i>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            className="ln-app-file-upload"
                            style={{
                              border: errors.profilePhoto
                                ? "2px dashed #ef4444"
                                : "2px dashed #cbd5e1",
                              padding: "15px",
                              borderRadius: "10px",
                              textAlign: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="file"
                              id="profilePhotoModal"
                              accept="image/*"
                              onChange={handleProfilePhotoChange}
                              style={{ display: "none" }}
                            />
                            <label
                              htmlFor="profilePhotoModal"
                              style={{ cursor: "pointer", display: "block" }}
                            >
                              <i
                                className="fas fa-camera"
                                style={{
                                  fontSize: "20px",
                                  color: errors.profilePhoto
                                    ? "#ef4444"
                                    : "#4F46E5",
                                  marginBottom: "8px",
                                }}
                              ></i>
                              <p
                                style={{
                                  margin: "8px 0",
                                  color: "#475569",
                                  fontWeight: "500",
                                  fontSize: "14px",
                                }}
                              >
                                Upload Photo
                              </p>
                              <p
                                style={{
                                  margin: "5px 0",
                                  color: "#94a3b8",
                                  fontSize: "11px",
                                }}
                              >
                                JPG, PNG (Max 2MB)
                              </p>
                            </label>
                          </div>
                          {errors.profilePhoto && (
                            <p
                              style={{
                                marginTop: "8px",
                                color: "#ef4444",
                                fontSize: "12px",
                              }}
                            >
                              {errors.profilePhoto}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Salary Slips */}
                    <div className="ln-app-group">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          className="ln-app-label"
                          style={{ marginBottom: 0 }}
                        >
                          Salary Slips (Last 6 Months)
                        </label>
                        <button
                          type="button"
                          onClick={addSalarySlip}
                          disabled={salarySlips.length >= 6}
                          style={{
                            padding: "6px 12px",
                            background:
                              salarySlips.length >= 6 ? "#94a3b8" : "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor:
                              salarySlips.length >= 6
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <i className="fas fa-plus"></i> Add Slip
                        </button>
                      </div>
                      {salarySlips.map((slip, index) => (
                        <div
                          key={slip.id}
                          style={{
                            marginBottom: "15px",
                            padding: "15px",
                            background: "#f8fafc",
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "15px",
                              alignItems: "flex-start",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <select
                                value={slip.month}
                                onChange={(e) =>
                                  handleSalarySlipMonthChange(
                                    slip.id,
                                    e.target.value,
                                  )
                                }
                                className="ln-app-select"
                                style={{ width: "100%" }}
                              >
                                <option value="">Select Month</option>
                                <option value="January">January</option>
                                <option value="February">February</option>
                                <option value="March">March</option>
                                <option value="April">April</option>
                                <option value="May">May</option>
                                <option value="June">June</option>
                                <option value="July">July</option>
                                <option value="August">August</option>
                                <option value="September">September</option>
                                <option value="October">October</option>
                                <option value="November">November</option>
                                <option value="December">December</option>
                              </select>
                            </div>
                            <div style={{ flex: 2 }}>
                              <div
                                className="ln-app-file-upload"
                                style={{
                                  border: "2px dashed #cbd5e1",
                                  padding: "12px",
                                  borderRadius: "8px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="file"
                                  id={`salarySlipModal-${slip.id}`}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) =>
                                    handleSalarySlipFileChange(slip.id, e)
                                  }
                                  style={{ display: "none" }}
                                />
                                <label
                                  htmlFor={`salarySlipModal-${slip.id}`}
                                  style={{
                                    cursor: "pointer",
                                    display: "block",
                                  }}
                                >
                                  <i
                                    className="fas fa-file-invoice"
                                    style={{
                                      fontSize: "18px",
                                      color: "#4F46E5",
                                      marginBottom: "6px",
                                    }}
                                  ></i>
                                  <p
                                    style={{
                                      margin: "6px 0",
                                      color: "#475569",
                                      fontWeight: "500",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Upload Slip
                                  </p>
                                  <p
                                    style={{
                                      margin: "4px 0",
                                      color: "#94a3b8",
                                      fontSize: "11px",
                                    }}
                                  >
                                    Max 5MB
                                  </p>
                                </label>
                              </div>
                              {slip.fileName && (
                                <p
                                  style={{
                                    marginTop: "8px",
                                    color: "#10b981",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ {slip.fileName}
                                </p>
                              )}
                            </div>
                            {salarySlips.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSalarySlip(slip.id)}
                                style={{
                                  background: "#ef4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  width: "36px",
                                  height: "36px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Offer Letter */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Upload Offer Letter
                      </label>
                      <div
                        className="ln-app-file-upload"
                        style={{
                          border: "2px dashed #cbd5e1",
                          padding: "20px",
                          borderRadius: "10px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="file"
                          id="offerLetterModal"
                          accept=".pdf,.doc,.docx"
                          onChange={handleOfferLetterChange}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor="offerLetterModal"
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          <i
                            className="fas fa-file-contract"
                            style={{
                              fontSize: "24px",
                              color: "#4F46E5",
                              marginBottom: "10px",
                            }}
                          ></i>
                          <p
                            style={{
                              margin: "10px 0",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            Upload Offer Letter
                          </p>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            PDF, DOC or DOCX (Max 5MB)
                          </p>
                        </label>
                      </div>
                      {offerLetterFileName && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#10b981",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {offerLetterFileName}
                        </p>
                      )}
                    </div>

                    {/* Cover Letter */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">Cover Letter</label>
                      <textarea
                        id="coverLetterModal"
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        className="ln-app-input"
                        rows="4"
                        placeholder="Tell us why you're interested in this position..."
                        style={{ resize: "vertical", minHeight: "100px" }}
                      />
                    </div>

                    {/* LinkedIn & Portfolio */}
                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">LinkedIn Profile</label>
                        <input
                          type="url"
                          id="linkedinModal"
                          name="linkedin"
                          value={formData.linkedin}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Portfolio/Website
                        </label>
                        <input
                          type="url"
                          id="portfolioModal"
                          name="portfolio"
                          value={formData.portfolio}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="https://yourportfolio.com"
                        />
                      </div>
                    </div>

                    {/* Additional Documents */}
                    <div className="ln-app-group">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          className="ln-app-label"
                          style={{ marginBottom: 0 }}
                        >
                          Additional Documents
                        </label>
                        <button
                          type="button"
                          onClick={addAdditionalDocument}
                          style={{
                            padding: "6px 12px",
                            background: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <i className="fas fa-plus"></i> Add Document
                        </button>
                      </div>
                      {additionalDocs.map((doc) => (
                        <div
                          key={doc.id}
                          style={{
                            marginBottom: "15px",
                            padding: "15px",
                            background: "#f8fafc",
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "15px",
                              alignItems: "flex-start",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <select
                                value={doc.type}
                                onChange={(e) =>
                                  handleAdditionalDocTypeChange(
                                    doc.id,
                                    e.target.value,
                                  )
                                }
                                className="ln-app-select"
                                style={{ width: "100%" }}
                              >
                                <option value="">Select Document Type</option>
                                <option value="Experience Certificate">
                                  Experience Certificate
                                </option>
                                <option value="Education Certificate">
                                  Education Certificate
                                </option>
                                <option value="Salary Slip">Salary Slip</option>
                                <option value="PAN Card">PAN Card</option>
                                <option value="Aadhaar Card">
                                  Aadhaar Card
                                </option>
                                <option value="Bank Statement">
                                  Bank Statement
                                </option>
                                <option value="Other">Other Document</option>
                              </select>
                            </div>
                            <div style={{ flex: 2 }}>
                              <div
                                className="ln-app-file-upload"
                                style={{
                                  border: "2px dashed #cbd5e1",
                                  padding: "12px",
                                  borderRadius: "8px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="file"
                                  id={`additionalDocModal-${doc.id}`}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  onChange={(e) =>
                                    handleAdditionalDocFileChange(doc.id, e)
                                  }
                                  style={{ display: "none" }}
                                />
                                <label
                                  htmlFor={`additionalDocModal-${doc.id}`}
                                  style={{
                                    cursor: "pointer",
                                    display: "block",
                                  }}
                                >
                                  <i
                                    className="fas fa-cloud-upload-alt"
                                    style={{
                                      fontSize: "18px",
                                      color: "#4F46E5",
                                      marginBottom: "6px",
                                    }}
                                  ></i>
                                  <p
                                    style={{
                                      margin: "6px 0",
                                      color: "#475569",
                                      fontWeight: "500",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Choose file
                                  </p>
                                  <p
                                    style={{
                                      margin: "4px 0",
                                      color: "#94a3b8",
                                      fontSize: "11px",
                                    }}
                                  >
                                    Max 5MB
                                  </p>
                                </label>
                              </div>
                              {doc.fileName && (
                                <p
                                  style={{
                                    marginTop: "8px",
                                    color: "#10b981",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ {doc.fileName}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAdditionalDocument(doc.id)}
                              style={{
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Terms & Conditions */}
                    <div className="ln-app-group">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                          padding: "15px",
                          background: "#f1f5f9",
                          borderRadius: "10px",
                          border: errors.terms
                            ? "2px solid #ef4444"
                            : "2px solid transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="termsModal"
                          name="terms"
                          checked={formData.terms}
                          onChange={handleInputChange}
                          style={{
                            width: "18px",
                            height: "18px",
                            marginTop: "2px",
                            cursor: "pointer",
                            accentColor: errors.terms ? "#ef4444" : "#4F46E5",
                          }}
                        />
                        <label
                          htmlFor="termsModal"
                          style={{
                            fontSize: "13px",
                            color: "#475569",
                            lineHeight: "1.5",
                            cursor: "pointer",
                            margin: 0,
                          }}
                        >
                          I agree to the{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#4F46E5", textDecoration: "none" }}
                          >
                            Privacy Policy
                          </a>{" "}
                          and consent to being contacted for recruitment
                          purposes <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                      </div>
                      {errors.terms && (
                        <span className="ln-app-error">{errors.terms}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* navigation buttons at bottom of modal form */}
                <div className="ln-app-actions">
                  {careerStep > 1 && (
                    <button
                      type="button"
                      onClick={goToPrevStep}
                      className="ln-btn-secondary"
                    >
                      ← Back
                    </button>
                  )}
                  <div className="ln-spacer"></div>
                  {careerStep < 2 && (
                    <button
                      type="button"
                      onClick={goToNextStep}
                      className="ln-btn-primary"
                    >
                      Next →
                    </button>
                  )}
                  {careerStep === 2 && (
                    <button
                      type="submit"
                      className="ln-btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit Application"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <header className="career-hero">
        <div className="career-hero-container">
          <div className="career-hero-content">
            <h1 className="career-hero-title">
              <span className="career-gradient-text">Build Your Future</span>
            </h1>
            <p className="career-hero-subtitle">
              Join India's fastest growing financial services platform. Grow
              your career with endless opportunities, industry-best training,
              and a culture that celebrates success.
            </p>

            <div className="career-hero-stats">
              <div className="career-stat-item">
                <div className="career-stat-number">150+</div>
                <div className="career-stat-label">Open Positions</div>
              </div>
              <div className="career-stat-item">
                <div className="career-stat-number">₹5L-₹25L</div>
                <div className="career-stat-label">Annual CTC Range</div>
              </div>
              <div className="career-stat-item">
                <div className="career-stat-number">4.8★</div>
                <div className="career-stat-label">Employee Rating</div>
              </div>
              <div className="career-stat-item">
                <div className="career-stat-number">50+</div>
                <div className="career-stat-label">Cities Across India</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="career-why-join-section">
        <div className="career-container">
          <div className="career-section-header">
            <h2>Why Join Krishna Finconcept?</h2>
            <p>
              We offer more than just a job - we offer a career with purpose and
              growth
            </p>
          </div>

          <div className="career-benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="career-benefit-card">
                <div className="career-benefit-icon">
                  <i className={benefit.icon}></i>
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="career-openings-section">
        <div className="career-container">
          <div className="career-section-header">
            <h2>Current Open Positions</h2>
            <p>
              Explore exciting career opportunities across various departments
            </p>
          </div>

          <div className="career-department-tabs">
            {departments.map((dept) => (
              <button
                key={dept.id}
                className={`career-tab-btn ${selectedDepartment === dept.id ? "career-active" : ""}`}
                onClick={() => setSelectedDepartment(dept.id)}
              >
                {dept.name}
              </button>
            ))}
          </div>

          <div className="career-job-openings-grid">
            {filteredJobs.map((job) => (
              <div key={job.id} className="career-job-card">
                <div className="career-job-tag">{job.tag}</div>
                <h3>{job.title}</h3>
                <div className="career-job-meta">
                  <span className="career-location">
                    <i className="fas fa-map-marker-alt"></i> {job.location}
                  </span>
                  <span className="career-experience">
                    <i className="fas fa-briefcase"></i> {job.experience}
                  </span>
                  <span className="career-salary">
                    <i className="fas fa-rupee-sign"></i> {job.salary}
                  </span>
                </div>
                <p className="career-job-description">{job.description}</p>
                <button
                  className="career-apply-job-btn"
                  onClick={() => handleApplyClick(job.title)}
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>

          <div className="career-load-more">
            <button
              className="career-view-all-btn"
              onClick={() => setSelectedDepartment("all")}
            >
              View All 150+ Positions <i className="fas fa-arrow-right"></i>
            </button>
            <button
              className="career-apply-separate-btn"
              onClick={() => navigate("/job-seeker")}
              style={{
                marginLeft: "15px",
                background: "#10b981",
                borderColor: "#10b981"
              }}
            >
              Apply on Separate Page <i className="fas fa-external-link-alt"></i>
            </button>
          </div>
        </div>
      </section>

      {!isCareerModalOpen && (
        <section className="career-application-section">
          <div className="career-container">
            <div className="career-info-grid">
              <div className="career-info-card career-hr-contact-card">
                <div className="career-hr-contact-layout">
                  <div className="career-card-icon">
                    <i className="fas fa-headset"></i>
                  </div>
                  <div className="career-hr-contact-content">
                    <h3>HR Contact</h3>
                    <div className="career-contact-info">
                      <p>
                        <i className="fas fa-envelope"></i>{" "}
                        hr@krishnafinconcept.com
                      </p>
                      <p>
                        <i className="fas fa-phone"></i> 9909002220
                      </p>
                      <p>
                        <i className="fas fa-clock"></i> Mon-Fri, 10 AM - 6 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="career-info-card career-tips-card">
                <div className="career-tips-layout">
                  <div className="career-card-icon">
                    <i className="fas fa-lightbulb"></i>
                  </div>
                  <div className="career-tips-content">
                    <h3>Application Tips</h3>
                    <ul className="career-tips-list">
                      <li>
                        <i className="fas fa-check-circle"></i> Update your
                        resume
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i> Customize cover
                        letter
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i> Highlight
                        achievements
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i> Correct contact
                        details
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i> Mention notice
                        period
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="career-info-card career-hiring-process-card">
                <div className="career-card-icon">
                  <i className="fas fa-history"></i>
                </div>
                <h3>Hiring Process</h3>
                <div className="career-process-timeline">
                  <div className="career-process-step">
                    <div className="career-step-number">1</div>
                    <div className="career-step-content">
                      <h4>Application Review</h4>
                      <p>3-5 working days</p>
                    </div>
                  </div>
                  <div className="career-process-step">
                    <div className="career-step-number">2</div>
                    <div className="career-step-content">
                      <h4>HR Screening</h4>
                      <p>Telephonic interview</p>
                    </div>
                  </div>
                  <div className="career-process-step">
                    <div className="career-step-number">3</div>
                    <div className="career-step-content">
                      <h4>Technical Interview</h4>
                      <p>With department head</p>
                    </div>
                  </div>
                  <div className="career-process-step">
                    <div className="career-step-number">4</div>
                    <div className="career-step-content">
                      <h4>Final Offer</h4>
                      <p>Offer letter & onboarding</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="career-info-card">
                <div className="career-card-icon">
                  <i className="fas fa-question-circle"></i>
                </div>
                <h3
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    marginBottom: "25px",
                  }}
                >
                  FAQs
                </h3>
                <div className="career-faq-item">
                  <p>
                    <strong>How long does the hiring process take?</strong>
                  </p>
                  <p>Typically 2-3 weeks from application to offer.</p>
                </div>
                <div className="career-faq-item">
                  <p>
                    <strong>Do you provide training?</strong>
                  </p>
                  <p>Yes, comprehensive training is provided for all roles.</p>
                </div>
                <div className="career-faq-item">
                  <p>
                    <strong>Is remote work available?</strong>
                  </p>
                  <p>Selected positions offer remote/hybrid options.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {showSuccessModal && (
        <div className="career-modal" style={{ display: "flex" }}>
          <div className="career-modal-content">
            <div className="career-modal-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Application Submitted Successfully!</h3>
            <p>
              Thank you! Your application has been submitted successfully. Our
              HR team will contact you within 3-5 working days.
            </p>
            <div className="career-modal-actions">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="career-modal-btn"
              >
                Close
              </button>
              <button
                onClick={() => navigate("/")}
                className="career-modal-btn career-secondary"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="career-modal" style={{ display: "flex" }}>
          <div className="career-modal-content" style={{ maxWidth: "500px" }}>
            <div className="career-modal-icon" style={{ color: "#ef4444" }}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Oops! Something Went Wrong</h3>
            <p
              style={{
                color: "#64748b",
                lineHeight: "1.6",
                marginBottom: "10px",
              }}
            >
              {errorModalMessage}
            </p>
            <div className="career-modal-actions">
              <button
                onClick={() => setShowErrorModal(false)}
                className="career-modal-btn"
                style={{
                  background: "#ef4444",
                  borderColor: "#ef4444",
                  color: "white",
                }}
              >
                <i className="fas fa-times"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="cv-hero-section">
        <div className="hero-container">
          {/* Left Side: Text and Buttons */}
          <div className="hero-left">
            <div className="status-badge"></div>

            <h1 class="hero-title">
              Create your CV with <br />
              <span class="text-blue-italic">CV maker</span>
            </h1>

            <p class="hero-subtitle">
              The first step to a better job? A better CV. Only 2% of CVs win,
              and yours will be one of them. Build it now!
            </p>

            <button className="btn-filled" onClick={() => navigate("/resume")}>
              Create a New CV
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Career;
